<template>
  <ResponsiveLayout :progress="progress">
    <template #header_end><HeaderHelp /></template>
    <div
      class="download-link"
      data-qa="download-link"
    >
      <div
        v-if="showSpinner"
        class="download-link__spinner"
      >
        <Base64Spinner />
      </div>
      <div
        v-else
        class="download-link__content kds-main-wrapper"
      >
        <div class="kds-content-block">
          <p
            class="kds-typography-title-xlarge"
            v-html="headerText"
          />
          <p
            v-if="state !== STATES.IN_APP"
            class="kds-typography-paragraph-large"
            v-html="
              $t('eu__download_page__description', {
                email: euStore.formData.email,
              })
            "
          />
        </div>
        <BaseBanner
          v-if="state === STATES.DESKTOP_BROWSER"
          variant="info"
          class="download-link__banner"
        >
          <p class="kds-typography-ui-strong-large">
            {{ $t('eu__download_page__qr_explanation') }}
          </p>
          <img
            class="download-link__qr-code"
            :src="qrCodeUrl"
            alt="Download Kaia QR Code"
          />
        </BaseBanner>

        <div
          v-if="state !== STATES.IN_APP"
          class="download-link__store"
        >
          <StoreBadge
            v-if="showAppStoreLink"
            class="download-link__store-item"
            :lang="lang"
            app-type="ios"
            @click="handleStoreLinkClick('App Store')"
          />
          <StoreBadge
            v-if="showPlayStoreLink"
            class="download-link__store-item"
            :lang="lang"
            app-type="android"
            @click="handleStoreLinkClick('Google Play')"
          />
        </div>
        <div
          v-if="state === STATES.IN_APP"
          class="download-link__redirect-cta"
        >
          <BaseButton
            :text="$t('b2b_get_started_btn_text')"
            @click="redirect"
          />
        </div>
      </div>
      <div
        v-if="!showSpinner"
        class="download-link__image-container"
      >
        <img
          class="download-link__image"
          :src="todayTabImage"
          alt="kaia app image"
          :width="todayTabImageDimensions.width"
          :height="todayTabImageDimensions.height"
        />
      </div>
    </div>
  </ResponsiveLayout>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import useUserStore from '@shared/stores/useUserStore.js'
import { likelyHasAppStore, likelyHasPlayStore } from '@shared/utils'
import { isFromApp } from '@shared/helpers/isFromApp'
import { useI18n } from 'petite-vue-i18n'

import getQRCodeUrl from '@shared/helpers/getQRCodeUrl.js'

import todayTabDe from '@shared/assets/img/today_tab_de.png'
import todayTabEn from '@shared/assets/img/today_tab_en.png'
import Base64Spinner from '@shared/components/Base64Spinner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import Tracker from '@shared/Tracker'
import StoreBadge from '@shared/components/StoreBadge.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import useDeepLink, {
  DEEP_LINK_CONFIGS,
} from '@shared/composables/useDeepLink.js'
import useEuStore from '@eu/app/stores/useEuStore.js'

const { t } = useI18n()
const userStore = useUserStore()
const euStore = useEuStore()
const { progress } = useOnboardingProgress()
const { redirect, ensureDeepLink } = useDeepLink(DEEP_LINK_CONFIGS.authToken)

const STATES = {
  IN_APP: 0,
  MOBILE_BROWSER: 1,
  DESKTOP_BROWSER: 2,
}

const state = isFromApp()
  ? STATES.IN_APP
  : likelyHasAppStore() || likelyHasPlayStore()
    ? STATES.MOBILE_BROWSER
    : STATES.DESKTOP_BROWSER
const showPlayStoreLink = !isFromApp() && !likelyHasAppStore()
const showAppStoreLink = !isFromApp() && !likelyHasPlayStore()

const showSpinner = ref(false)
const lang = computed(() => userStore.lang)
const todayTabImage = computed(() =>
  lang.value.startsWith('de') ? todayTabDe : todayTabEn,
)
const todayTabImageDimensions = computed(() =>
  lang.value.startsWith('de')
    ? { width: 592, height: 1549 }
    : { width: 592, height: 1571 },
)

const qrCodeUrl = computed(() => getQRCodeUrl(lang.value))

const headerText = computed(() =>
  state === STATES.IN_APP
    ? t('eu__download_page__header__deeplink__with_user')
    : t('eu__download_page__header'),
)

const handleStoreLinkClick = (storeName) => {
  Tracker.trackButtonSelect(`Go to ${storeName}`, 'signup_app_download')
}

onMounted(async () => {
  if (state === STATES.IN_APP) {
    showSpinner.value = true
    await ensureDeepLink()

    setTimeout(() => {
      showSpinner.value = false
    }, 2000)

    await redirect()
  }
})
</script>

<style lang="scss">
.download-link {
  display: flex;
  flex-direction: column-reverse;
  gap: $kds-space-3x;

  &__spinner {
    width: 64px;
    height: 64px;
    margin: $kds-space-2x auto 0;
  }

  &__image-container {
    display: flex;
    justify-content: center;
    width: 100%;
    max-width: 115px;
    margin-inline: auto;
  }

  &__content {
    min-width: 0;
    flex: 1 1 auto;
  }

  &__image {
    display: block;
    width: 100%;
    height: auto;
  }

  &__qr-code {
    width: 96px;
    height: 96px;
    margin-left: auto;
    flex: 0 0 96px;
  }

  &__store {
    display: flex;
    gap: $kds-space-1x;
    justify-content: center;
    flex-wrap: wrap;

    &-item {
      height: 48px;
    }
  }

  &__banner .base-banner__description {
    display: flex;
    align-items: center;
    gap: $kds-space-2x;

    p {
      margin: 0;
    }
  }

  @include media-up(md) {
    align-items: center;
    flex-direction: row;
    gap: $kds-space-4x;

    &__image-container {
      flex: 0 0 207px;
      width: 207px;
      max-width: none;
      margin-inline: 0;
    }

    &__image {
      width: 70%;
    }
  }
}
</style>
